import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import {
  localUninstallCommand, NODE_REMOVAL_COLUMNS, removalView, removeComputer,
  type NodeRemovalRow, type RemoveComputerResult,
} from '../lib/computer-removal';

/**
 * The one owner control for taking a computer out of Brunel. It asks for the computer's own
 * name as confirmation and goes through the same verified owner session as other protected
 * actions; the database checks both again.
 */
export function RemoveComputer({ client, guard, orgId, nodeId, nodeName, onChanged }: {
  client: SupabaseClient; guard: Guard; orgId: string; nodeId: string; nodeName: string; onChanged: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RemoveComputerResult | null>(null);
  const matches = typed.trim() === nodeName;
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!matches || busy) return;
    setBusy(true);
    void removeComputer({
      orgId, nodeId, nodeName, confirmName: typed.trim(), guard,
      rpc: (name, args) => client.rpc(name, args),
      refresh: onChanged,
      onResult: (next) => { setResult(next); if (next.ok) setTyped(''); },
    }).finally(() => setBusy(false));
  };
  // A disclosure, not a button: closed, it adds nothing to press by accident.
  return (
    <details className="remove-computer">
      <summary title={`Take ${nodeName} out of Brunel and remove Brunel from it`}>Remove this computer</summary>
      <form className="stack" onSubmit={onSubmit}>
        <small className="muted">
          {nodeName} stops taking work at once, its Workers are released and it leaves this list. Brunel then removes its own
          service, program, logs, profile and credentials from it. Project work folders on it are kept. Other computers are not touched.
        </small>
        <div className="row">
          <input aria-label={`Type ${nodeName} to confirm`} placeholder={`Type ${nodeName} to confirm`} maxLength={64}
            value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
          <button type="submit" className="primary" disabled={!matches || busy}>Remove {nodeName}</button>
        </div>
        {result && <p role={result.ok ? 'status' : 'alert'}>{result.message}</p>}
      </form>
    </details>
  );
}

/** Computers that were removed, and truthfully how far each one's cleanup got. */
export function RemovedComputers({ client, orgId, refreshKey }: { client: SupabaseClient; orgId: string; refreshKey: number }) {
  const [rows, setRows] = useState<readonly NodeRemovalRow[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const load = useCallback(async () => {
    const { data, error } = await client.schema('brunel').from('node_removal_status')
      .select(NODE_REMOVAL_COLUMNS).eq('org_id', orgId).order('requested_at', { ascending: false }).limit(20);
    if (!error && Array.isArray(data)) setRows(data as unknown as NodeRemovalRow[]);
  }, [client, orgId]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  // While a cleanup is still in flight, look again; a settled list is left alone.
  const unsettled = rows.some((row) => !removalView(row).settled);
  useEffect(() => {
    if (!unsettled) return undefined;
    const timer = window.setTimeout(() => { void load(); }, 15_000);
    return () => window.clearTimeout(timer);
  }, [unsettled, rows, load]);
  if (rows.length === 0) return null;
  const copy = async (id: string, command: string) => {
    await navigator.clipboard.writeText(command);
    setCopied(id);
    window.setTimeout(() => setCopied(null), 2000);
  };
  return (
    <div className="settings-block" aria-label="Removed computers">
      <h2>Removed computers<span className="count">{rows.length}</span></h2>
      <ul className="list">
        {rows.map((row) => {
          const view = removalView(row);
          const local = localUninstallCommand(window.location.origin, row.host_kind);
          return (
            <li key={row.id} className="stack">
              <div className="row spread">
                <span><span className={`dot dot-${view.tone}`} aria-hidden="true" /> {row.node_name}</span>
                <span className="muted">{view.label}</span>
              </div>
              <small className="muted">{view.detail}</small>
              {view.localCommand && (
                <>
                  <small className="muted">On {row.node_name}, open {local.shell} and paste:</small>
                  <code className="block">{local.command}</code>
                  <div className="row">
                    <button type="button" onClick={() => void copy(row.id, local.command)}>{copied === row.id ? 'Copied' : 'Copy command'}</button>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
